import { createEffect } from 'effector'
import { nanoid } from 'nanoid'
import urlJoin from 'url-join'

import * as popup from '../popup'

type GetAccessTokenParams = {
    lang: string
    clientID: string
    redirectURI: string
}

const POPUP_TIMEOUT = 3 * 60 * 1000 // 3 minutes

export const getAccessTokenFx = createEffect<GetAccessTokenParams, { code: string } | null>(
    async ({ lang, clientID, redirectURI }) => {
        const oAuthState = nanoid()
        const yahooPopupURL = getYahooPopupURL({ state: oAuthState, targetLang: lang, clientID, redirectURI })

        const popupWindow = popup.open({
            url: `/oauth-bridge/#${encodeURIComponent(yahooPopupURL)}`,
            title: 'login_popup',
            width: 400,
            height: 590,
            resizable: true,
            scrollbars: true,
            toolbar: true,
            menubar: true,
            location: false,
            status: false,
        })
        if (!popupWindow) {
            return null
        }

        popupWindow.focus()

        const controller = new AbortController()
        const { signal } = controller

        return await Promise.race([
            waitForOAuthMessage(popupWindow, oAuthState, signal),
            popup.waitForClose(popupWindow, signal),
            popup.waitForTimeout(signal, POPUP_TIMEOUT),
        ]).finally(() => controller.abort())
    }
)

function waitForOAuthMessage(popup: Window, oAuthState: string, signal: AbortSignal): Promise<{ code: string }> {
    return new Promise((resolve) => {
        const onMessage = (e: MessageEvent) => {
            if (isOAuthResponse(e.data) && e.data.state === oAuthState) {
                popup.close()
                bc.close()
                resolve({ code: e.data.code })
            }
        }

        // NOTE: Listen for postMessage (if opener survived)
        window.addEventListener('message', onMessage, { signal })

        // NOTE: BroadcastChannel fallback
        const bc = new BroadcastChannel('oauth_popup_channel')
        bc.onmessage = onMessage

        signal.addEventListener('abort', () => bc.close())
    })
}

type OAuthResponse = {
    code: string
    state: string
}

function isOAuthResponse(data: any): data is OAuthResponse {
    if (!data.state) {
        return false
    }

    if (!data.code) {
        return false
    }

    return true
}

const languageMap: { [key: string]: string } = {
    cn: 'zh-CN',
    tw: 'zh-TW',
}

export const getRedirectURI = (portalURL: string) => urlJoin(portalURL, '/oauth/')

type GetPopupURLArgs = {
    state: string
    targetLang: string
    clientID: string
    redirectURI: string
}

function getYahooPopupURL(args: GetPopupURLArgs) {
    const { state, targetLang, clientID, redirectURI } = args

    const language = languageMap[targetLang] || targetLang

    const params = new URLSearchParams({
        client_id: clientID,
        redirect_uri: redirectURI,
        response_type: 'code',
        state,
        language,
    })

    return `https://api.login.yahoo.com/oauth2/request_auth?${params.toString()}`
}
